import { forwardRef, useCallback, useImperativeHandle, useRef, type ComponentProps, type UIEvent } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { tokenize, type TemplateVariableKind } from '@/lib/templates';
import { cn } from '@/lib/utils';

/**
 * A textarea whose `{{variables}}` are drawn as tinted chips while you type. The textarea itself keeps every
 * behaviour (caret, selection, undo, resize) but its glyphs are transparent; a mirror behind it renders the
 * same text with the tokens highlighted. The chips change colour and background only, never glyph metrics, so
 * the mirror and the textarea line up character for character. Scrolling is mirrored on every scroll event.
 */
export const VariableTextarea = forwardRef<HTMLTextAreaElement, ComponentProps<typeof Textarea> & { value: string }>(function VariableTextarea({ value, className, onScroll, ...props }, ref) {
  const inner = useRef<HTMLTextAreaElement | null>(null);
  const mirror = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(ref, () => inner.current as HTMLTextAreaElement);
  const sync = useCallback(() => {
    if (mirror.current && inner.current) {
      mirror.current.scrollTop = inner.current.scrollTop;
      mirror.current.scrollLeft = inner.current.scrollLeft;
    }
  }, []);
  const parts = tokenize(value);
  return (
    <div className="relative grid dark:bg-input/30">
      <div ref={mirror} aria-hidden className={cn(LAYOUT, 'pointer-events-none absolute inset-0 overflow-hidden border-transparent break-words whitespace-pre-wrap text-foreground', className)}>
        {parts.map((p, i) =>
          p.type === 'text' ? (
            <span key={i}>{p.value}</span>
          ) : (
            <span key={i} className={cn('rounded-[3px]', CHIP[p.kind])}>
              {p.raw}
            </span>
          ),
        )}
        {/* A trailing newline needs a glyph after it or the mirror is one line short of the textarea. */}
        {value.endsWith('\n') ? <span>&#8203;</span> : null}
      </div>
      <Textarea
        {...props}
        ref={inner}
        value={value}
        onScroll={(e: UIEvent<HTMLTextAreaElement>) => {
          sync();
          onScroll?.(e);
        }}
        className={cn(className, 'relative bg-transparent text-transparent caret-foreground selection:bg-primary/20 selection:text-transparent placeholder:text-muted-foreground dark:bg-transparent')}
        spellCheck={false}
      />
    </div>
  );
});

/** The base textarea's box and type, repeated on the mirror so the two wrap identically (see ui/textarea.tsx). */
const LAYOUT = 'min-h-16 w-full border px-3 py-2 text-base md:text-sm';

/** Background and ink per kind; box-shadow draws the outline so the token's width never changes. */
const CHIP: Record<TemplateVariableKind, string> = {
  builtin: 'bg-muted text-foreground shadow-[0_0_0_1px_var(--border)]',
  contact: 'bg-accent text-primary shadow-[0_0_0_1px_color-mix(in_oklch,var(--primary)_40%,transparent)]',
  custom: 'bg-warning/15 text-foreground shadow-[0_0_0_1px_color-mix(in_oklch,var(--warning)_60%,transparent)]',
};
